import { motion } from "framer-motion";
import { Target, CheckCircle2 } from "lucide-react";

const fadeUp = {
  hidden: { opacity: 0, y: 40 },
  show: { opacity: 1, y: 0, transition: { duration: 0.8, ease: [0.16, 1, 0.3, 1] } },
};

const AIMS = [
  "Provide high-quality, individualised physiotherapy services that address each patient's unique needs and personal goals.",
  "Optimise physical performance, independence and quality of life.",
  "Empower our patients to take an active role in their health and rehabilitation.",
  "Promote overall long-term health and injury prevention for every age group.",
];

const OBJECTIVES = [
  "Conduct an in-depth, comprehensive and individualised assessment to develop a unique treatment programme for every patient.",
  "Reassess patient progress at clinically appropriate intervals and document changes using validated outcome measures throughout the rehabilitation programme.",
  "Provide every patient with individualised education and self-management strategies during their rehabilitation.",
  "Maintain multidisciplinary referral networks with general practitioners, medical specialists, dietitians and more, ensuring holistic rehabilitation.",
  "Establish professional referral relationships with at least five local healthcare professionals, gyms or sporting organisations within the first six months of practice.",
  "Ensure the highest quality of support and treatment through continuous professional development and relevant training.",
];

export const Manifesto = () => (
  <section id="about" data-testid="manifesto-section" className="bg-navy pt-28 lg:pt-40 pb-16 lg:pb-24 relative overflow-hidden">
    <div className="absolute -top-40 -right-40 w-[560px] h-[560px] rounded-full bg-electric/15 blur-[120px] pointer-events-none" />
    <div className="max-w-7xl mx-auto px-6 lg:px-12 relative z-10">
      <motion.p
        variants={fadeUp}
        initial="hidden"
        whileInView="show"
        viewport={{ once: true, margin: "-80px" }}
        className="font-display font-extrabold text-2xl sm:text-3xl tracking-tight text-electric mb-16"
      >
        Who We Are
      </motion.p>

      <div className="grid lg:grid-cols-12 gap-12 lg:gap-16">
        <motion.div
          variants={fadeUp}
          initial="hidden"
          whileInView="show"
          viewport={{ once: true, margin: "-80px" }}
          className="lg:col-span-5"
          data-testid="mission-chapter"
        >
          <span className="font-display font-black text-7xl lg:text-8xl text-white/10 leading-none">01</span>
          <h2 className="font-display font-extrabold text-4xl sm:text-5xl tracking-tighter text-white mt-2 mb-6">Our Mission</h2>
          <p className="text-white/75 text-base lg:text-lg leading-relaxed border-l-2 border-electric pl-6">
            To empower people through movement. As a patient-centred physiotherapy practice, we provide
            accessible, evidence-based rehabilitation tailored to each individual's needs — helping our
            patients restore function, build confidence, maintain independence and live healthier,
            fulfilling lives.
          </p>
        </motion.div>

        <motion.div
          variants={fadeUp}
          initial="hidden"
          whileInView="show"
          viewport={{ once: true, margin: "-80px" }}
          className="lg:col-span-6 lg:col-start-7 lg:mt-40"
          data-testid="vision-chapter"
        >
          <span className="font-display font-black text-7xl lg:text-8xl text-white/10 leading-none">02</span>
          <h2 className="font-display font-extrabold text-4xl sm:text-5xl tracking-tighter text-white mt-2 mb-6">Our Vision</h2>
          <p className="text-white/75 text-base lg:text-lg leading-relaxed border-l-2 border-electric pl-6">
            To become a trusted and leading physiotherapy practice in Cape Town, recognised for
            accessible, innovative and evidence-based rehabilitation that empowers people to move
            confidently and live healthier, more active lives.
          </p>
        </motion.div>
      </div>

      <motion.div
        variants={fadeUp}
        initial="hidden"
        whileInView="show"
        viewport={{ once: true, margin: "-80px" }}
        className="mt-28 lg:mt-40"
        data-testid="goals-chapter"
      >
        <div className="rounded-2xl p-8 sm:p-12 lg:p-16 bg-gradient-to-br from-electric via-[#0064d6] to-[#0047ab] shadow-[0_32px_96px_rgba(0,122,255,0.35)]">
          <div className="flex flex-col lg:flex-row lg:items-end justify-between gap-6 mb-14">
            <div>
              <span className="font-display font-black text-7xl lg:text-8xl text-white/15 leading-none">03</span>
              <h2 className="font-display font-extrabold text-4xl sm:text-5xl tracking-tighter text-white mt-2">Aims & Objectives</h2>
            </div>
            <p className="max-w-sm text-white/75 text-sm lg:text-base">
              The aims that guide our care — and the SMART objectives we hold ourselves accountable to.
            </p>
          </div>

          <p className="text-xs font-bold tracking-[0.3em] uppercase text-white mb-6">Our Aims</p>
          <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-px bg-white/25 border border-white/25 mb-16">
            {AIMS.map((aim, i) => (
              <motion.div
                key={i}
                initial={{ opacity: 0, y: 24 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: i * 0.08, duration: 0.6 }}
                className="group bg-white/[0.07] backdrop-blur-sm p-8 hover:bg-white transition-colors duration-500"
                data-testid={`aim-card-${i}`}
              >
                <div className="mb-6">
                  <Target size={22} className="text-white group-hover:text-electric transition-colors duration-500" />
                </div>
                <p className="text-sm leading-relaxed text-white/85 group-hover:text-ink transition-colors duration-500">
                  {aim}
                </p>
              </motion.div>
            ))}
          </div>

          <p className="text-xs font-bold tracking-[0.3em] uppercase text-white mb-6">SMART Objectives</p>
          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-px bg-white/25 border border-white/25">
            {OBJECTIVES.map((goal, i) => (
              <motion.div
                key={i}
                initial={{ opacity: 0, y: 24 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: i * 0.08, duration: 0.6 }}
                className="group bg-white/[0.07] backdrop-blur-sm p-8 lg:p-10 hover:bg-navy transition-colors duration-500"
                data-testid={`objective-card-${i}`}
              >
                <div className="mb-6">
                  <CheckCircle2 size={22} className="text-white transition-colors duration-500" />
                </div>
                <p className="text-sm lg:text-base leading-relaxed text-white/85 group-hover:text-white transition-colors duration-500">
                  {goal}
                </p>
              </motion.div>
            ))}
          </div>
        </div>
      </motion.div>
    </div>
  </section>
);
